import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Eye, EyeOff, KeyRound, CheckCircle2, AlertTriangle, ArrowLeft,
} from 'lucide-react';
import { resetPassword } from '@/lib/api';
import { AuthLayout } from '@/components/AuthLayout';
import formStyles from '@/components/AuthForm.module.css';
import loginStyles from '@/pages/login/LoginPage.module.css';

/**
 * Password complexity matches the existing project convention:
 * minimum 8 chars. Stricter rules (mixed case, digits, symbols)
 * deliberately not enforced — the backend's hashPassword is
 * Argon2id at production parameters; entropy at 8+ chars is well
 * within acceptable bounds for non-financial admin auth. If the
 * backend ever adds stricter rules to /auth/reset-password, the
 * server returns a validation error that surfaces in serverError.
 *
 * confirmPassword cross-field check uses Zod's refine to surface
 * inline on the confirm field rather than as a server error
 * (better UX — caught before the round trip).
 */
const schema = z
  .object({
    password: z.string().min(8, 'At least 8 characters'),
    confirmPassword: z.string(),
  })
  .refine((v) => v.password === v.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });
type FormValues = z.infer<typeof schema>;

interface Props {
  onNavigate: (path: string) => void;
}

/**
 * Admin password-reset CONSUMPTION form (AAP.3). Reads `token` +
 * `email` from URL query params placed there by the email link
 * AAP.1 templated against ADMIN_ORIGIN. User enters new password
 * twice; submit consumes the token + sets the new password via
 * /auth/reset-password.
 *
 * URL parse runs once on mount via useMemo (params won't change
 * during the page lifecycle; useState would also work but useMemo
 * avoids the initializer rerun on remount cycles).
 *
 * Three top-level states: 'missing' (token/email absent from URL),
 * 'form' (default — user enters new password), 'done' (post-submit
 * success). Server failures stay in 'form' state with serverError
 * populated, so the user can retry without losing the page.
 *
 * The password-show-toggle composition reuses .passwordWrapper +
 * .passwordInput + .eyeBtn from LoginPage.module.css — the
 * one-of-two consumers pattern documented in AuthForm.module.css's
 * header. Extract to AuthForm if a third password-input page joins.
 */
export function ResetPasswordPage({ onNavigate }: Props) {
  const params = useMemo(() => {
    const sp = new URLSearchParams(window.location.search);
    return { email: sp.get('email'), token: sp.get('token') };
  }, []);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [serverError, setServerError] = useState('');
  const [done, setDone] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  // No useEffect-based redirect for missing params — render the
  // error state inline. Cleaner than a navigate() bounce; the user
  // can still tap "Request a new link" to go to /auth/forgot.

  const onSubmit = async (values: FormValues) => {
    setServerError('');
    if (!params.email || !params.token) {
      setServerError('Missing email or token');
      return;
    }
    try {
      await resetPassword(params.email, params.token, values.password);
      setDone(true);
    } catch (err) {
      setServerError(err instanceof Error ? err.message : 'Reset failed');
    }
  };

  // ── Missing token/email state ──────────────────────────────────
  if (!params.email || !params.token) {
    return (
      <AuthLayout
        title="Reset link broken"
        subtitle="The link is missing required information."
        footer={
          <a
            href="/login"
            onClick={(e) => {
              e.preventDefault();
              onNavigate('/login');
            }}
          >
            <ArrowLeft size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            Back to sign in
          </a>
        }
      >
        <div className={formStyles.serverError} role="alert">
          <AlertTriangle size={16} style={{ verticalAlign: 'middle', marginRight: 8 }} />
          Missing email or token in the link. Request a new password reset.
        </div>
        <button
          type="button"
          className={formStyles.submitBtn}
          onClick={() => onNavigate('/auth/forgot')}
        >
          Request a new reset link
        </button>
      </AuthLayout>
    );
  }

  // ── Done state ─────────────────────────────────────────────────
  if (done) {
    return (
      <AuthLayout
        title="Password updated"
        subtitle="You can sign in with your new password."
      >
        <div className={formStyles.formSuccess} role="status">
          <CheckCircle2 size={16} style={{ verticalAlign: 'middle', marginRight: 8 }} />
          Your password has been reset. Sign in below to continue.
        </div>
        <button
          type="button"
          className={formStyles.submitBtn}
          onClick={() => onNavigate('/login')}
        >
          Sign in
        </button>
      </AuthLayout>
    );
  }

  // ── Form state (default) ───────────────────────────────────────
  return (
    <AuthLayout
      title="Choose a new password"
      subtitle="Pick something you'll remember — at least 8 characters."
      footer={
        <a
          href="/login"
          onClick={(e) => {
            e.preventDefault();
            onNavigate('/login');
          }}
        >
          <ArrowLeft size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
          Back to sign in
        </a>
      }
    >
      <form className={formStyles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
        {serverError && (
          <div className={formStyles.serverError} role="alert">
            {serverError}
          </div>
        )}

        {/* New password */}
        <div className={formStyles.field}>
          <label htmlFor="password" className={formStyles.label}>
            <KeyRound size={12} style={{ verticalAlign: 'middle', marginRight: 6 }} />
            New password
          </label>
          <div className={loginStyles.passwordWrapper}>
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="••••••••••••"
              className={`${formStyles.input} ${loginStyles.passwordInput} ${errors.password ? formStyles.inputError : ''}`}
              {...register('password')}
            />
            <button
              type="button"
              className={loginStyles.eyeBtn}
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && (
            <p className={formStyles.error}>{errors.password.message}</p>
          )}
        </div>

        {/* Confirm password */}
        <div className={formStyles.field}>
          <label htmlFor="confirmPassword" className={formStyles.label}>
            Confirm password
          </label>
          <div className={loginStyles.passwordWrapper}>
            <input
              id="confirmPassword"
              type={showConfirm ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="••••••••••••"
              className={`${formStyles.input} ${loginStyles.passwordInput} ${errors.confirmPassword ? formStyles.inputError : ''}`}
              {...register('confirmPassword')}
            />
            <button
              type="button"
              className={loginStyles.eyeBtn}
              onClick={() => setShowConfirm((v) => !v)}
              aria-label={showConfirm ? 'Hide password' : 'Show password'}
            >
              {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.confirmPassword && (
            <p className={formStyles.error}>{errors.confirmPassword.message}</p>
          )}
        </div>

        <button
          type="submit"
          className={formStyles.submitBtn}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Updating…' : 'Update password'}
        </button>
      </form>
    </AuthLayout>
  );
}
